import React, {useEffect} from 'react';
import {EditorContent, useEditor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import {TableKit} from '@tiptap/extension-table';

const ContentImage = Image.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            dataFileId: {
                default: null,
                parseHTML: element => element.getAttribute('data-file-id'),
                renderHTML: attributes => attributes.dataFileId
                    ? {'data-file-id': attributes.dataFileId}
                    : {},
            },
        };
    },
});

const tools = [
    ['Bold', 'bold', editor => editor.chain().focus().toggleBold().run()],
    ['Italic', 'italic', editor => editor.chain().focus().toggleItalic().run()],
    ['Strike', 'strike', editor => editor.chain().focus().toggleStrike().run()],
    ['Heading 2', 'heading', editor => editor.chain().focus().toggleHeading({level: 2}).run()],
    ['Bulleted list', 'bulletList', editor => editor.chain().focus().toggleBulletList().run()],
    ['Numbered list', 'orderedList', editor => editor.chain().focus().toggleOrderedList().run()],
    ['Quote', 'blockquote', editor => editor.chain().focus().toggleBlockquote().run()],
    ['Code block', 'codeBlock', editor => editor.chain().focus().toggleCodeBlock().run()],
];

export default function RichTextEditor({
    value,
    onChange,
    onPasteImage,
    onInsertImage,
    label = 'Rich text content',
}) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            ContentImage.configure({allowBase64: false}),
            TableKit.configure({table: {resizable: false}}),
        ],
        content: value || '',
        immediatelyRender: false,
        editorProps: {
            attributes: {
                'aria-label': label,
                class: 'content-rich-text-editor-input',
            },
            handlePaste: (_view, event) => {
                const image = [...(event.clipboardData?.files || [])]
                    .find(file => file.type?.startsWith('image/'));
                if (!image || !onPasteImage) return false;
                event.preventDefault();
                onPasteImage(image);
                return true;
            },
            transformPastedHTML: html => html
                .replace(/\s(?:class|style|lang)=("[^"]*"|'[^']*')/gi, '')
                .replace(/<!--(?:.|\n|\r)*?-->/g, ''),
        },
        onUpdate: ({editor: nextEditor}) => onChange?.(nextEditor.getHTML()),
    });

    useEffect(() => {
        if (editor && editor.getHTML() !== (value || '')) {
            editor.commands.setContent(value || '', {emitUpdate: false});
        }
    }, [editor, value]);

    if (!editor) return <p role="status">Loading rich-text editor…</p>;

    return <div className="content-rich-text-editor">
        <div className="content-editor-toolbar" role="toolbar" aria-label="Text formatting">
            {onInsertImage && <button type="button" className="content-editor-tool-button"
                onClick={onInsertImage} aria-label="Insert image">Image</button>}
            {tools.map(([labelText, activeName, action]) => <button
                key={labelText}
                type="button"
                className="content-editor-tool-button"
                aria-label={labelText}
                title={labelText}
                aria-pressed={editor.isActive(activeName)}
                onClick={() => action(editor)}
            >{({Bold: <strong>B</strong>, Italic: <em>I</em>, Strike: <s>S</s>,
                'Heading 2': 'Heading', 'Bulleted list': '• List', 'Numbered list': '1. List',
                Quote: '❞', 'Code block': '</>'})[labelText]}</button>)}
            <button
                type="button"
                className="content-editor-tool-button"
                aria-pressed={editor.isActive('link')}
                onClick={() => {
                    const current = editor.getAttributes('link').href || '';
                    const href = window.prompt('Link URL', current);
                    if (href === null) return;
                    if (!href) editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    else editor.chain().focus().extendMarkRange('link').setLink({href}).run();
                }}
            >Link</button>
            <button
                type="button"
                className="content-editor-tool-button"
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
            >Divider</button>
            <button
                type="button"
                className="content-editor-tool-button"
                aria-label="Insert table"
                onClick={() => editor.chain().focus().insertTable({
                    rows: 3, cols: 3, withHeaderRow: true,
                }).run()}
            >Table</button>
            <button type="button" className="content-editor-tool-button"
                onClick={() => editor.chain().focus().undo().run()}>
                Undo
            </button>
            <button type="button" className="content-editor-tool-button"
                onClick={() => editor.chain().focus().redo().run()}>
                Redo
            </button>
        </div>
        <EditorContent editor={editor} />
    </div>;
}
